import React, {useState} from "react";
import DialogCloseButton from "../buttons/DialogCloseButton";
import {passwordRules} from "../../utils/passwordRules";
import PasswordRules from "../components/PasswordRules";
import {useTranslation} from "react-i18next";
import {checkPassword, updateAccount} from "../../api/accountApi";
import MessageBox, {MessageType} from "../components/MessageBox";
import {useRequiredAccount} from "../../hooks/AccountContext";

interface DialogData {
    oldPassword: string;
    newPassword: string;
    repeatPassword: string;
}

interface PasswordChangeDialogProps {
    open: boolean;
    onClose: () => void;
}

export default function PasswordChangeDialog({open, onClose}: PasswordChangeDialogProps) {
    const {t} = useTranslation();

    const [dialogData, setDialogData] = useState<DialogData>({
        oldPassword: "",
        newPassword: "",
        repeatPassword: "",
    });

    const {
        account,
        setAccount
    } = useRequiredAccount();

    // const [message, setMessage] = useState<{ type: "success" | "error"; text: string } | null>(null);
    const [message, setMessage] = useState<MessageType>(null);
    if (!open) return null;

    const passwordValid = passwordRules.every((r) => r.test(dialogData.newPassword));
    const allFilled =
        dialogData.oldPassword.trim() &&
        dialogData.newPassword.trim() &&
        dialogData.repeatPassword.trim();

    const passwordsMatch =
        dialogData.newPassword === dialogData.repeatPassword && dialogData.newPassword !== "" && dialogData.repeatPassword !== "";
    const isValid = Boolean(allFilled && passwordValid && passwordsMatch);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!isValid) return;

        try {
            if (!(await checkPassword(account.username, dialogData.oldPassword))) {
                setMessage({type: "error", text: t("auth.messages.oldPasswordWrong")});
                return;
            }
            const dto = {
                password: dialogData.newPassword,
            };
            const accountObj = await updateAccount(account.id, dto);
            setAccount(accountObj);
            handleClose();
        } catch (err: any) {
            setMessage({type: "error", text: t("auth.messages.passwordChangeFailed")});
        }
    };

    const resetDialog = () => {
        setDialogData({
            oldPassword: "",
            newPassword: "",
            repeatPassword: "",
        });
        setMessage(null);
    };

    const handleClose = () => {
        resetDialog();
        onClose();
    };

    return (
        <div className="dialog-overlay">
            <div className="dialog">
                <DialogCloseButton onClose={handleClose}/>

                <div>
                    <h3>{t("common.button.changePassword")}</h3>
                </div>

                <form onSubmit={handleSubmit} className="form-container">
                    <MessageBox message={message}/>

                    <div className="form-group">
                        <label>{t("password.fields.oldPassword")}</label>
                        <input
                            type="password"
                            className="form-input"
                            value={dialogData.oldPassword}
                            onChange={e => setDialogData({...dialogData, oldPassword: e.target.value})}
                        />
                    </div>

                    <div className="form-group">
                        <label>{t("password.fields.newPassword")}</label>
                        <input
                            type="password"
                            className="form-input"
                            value={dialogData.newPassword}
                            onChange={e => setDialogData({...dialogData, newPassword: e.target.value})}
                        />
                        <PasswordRules password={dialogData.newPassword}/>
                    </div>

                    <div className="form-group">
                        <label>{t("password.fields.repeatPassword")}</label>
                        <input
                            type="password"
                            className="form-input"
                            value={dialogData.repeatPassword}
                            onChange={e => setDialogData({...dialogData, repeatPassword: e.target.value})}
                        />
                        {dialogData.repeatPassword.length > 0
                            && !passwordsMatch && dialogData.repeatPassword.trim() !== "" && (
                                <small className="error-text">{t("auth.messages.passwordMismatch")}</small>
                            )}
                    </div>

                    <button
                        type="submit"
                        className="form-button"
                        disabled={!isValid}>
                        {t("common.button.save")}
                    </button>
                </form>
            </div>
        </div>
    );
}